import React, { useState, useEffect, useCallback, useRef } from 'react';
import StrictDateInput from '../components/StrictDateInput';
import { formatDateDMY } from '../utils/dateFormat';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { customerAPI, invoiceAPI, settingsAPI, bulkAPI, whatsappAPI, paymentAPI } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';
import { useData } from '../contexts/DataContext';
import logo from '../logo.png';
import { resolveMediaUrl } from '../utils/mediaUrl';

/* ── Customer Outstanding Component ─────────── */
const CustomerOutstanding = () => {
  const { darkMode, color } = useTheme();
  const acc = color.swatch;
  const card = { background: darkMode ? '#1E293B' : '#fff', border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, borderRadius: 12 };
  const pg = { color: darkMode ? '#F1F5F9' : '#0F172A' };
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    customerAPI.outstanding()
      .then(r => { setData(r.data || []); setError(null); setLoading(false); })
      .catch((err) => { setError(err.message || 'Failed to load outstanding data'); setLoading(false); });
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: 60, ...muted }}>Loading outstanding…</div>;

  if (error) return (
    <div style={{ ...card, padding: 60, textAlign: 'center' }}>
      <div style={{ fontSize: 48, marginBottom: 10 }}>⚠️</div>
      <p style={{ color: '#dc2626' }}>{error}</p>
    </div>
  );

  if (data.length === 0) return (
    <div style={{ ...card, padding: 60, textAlign: 'center' }}>
      <div style={{ fontSize: 48, marginBottom: 10 }}>🎉</div>
      <p style={{ ...muted }}>No outstanding balances — all customers are up to date!</p>
    </div>
  );

  const total = data.reduce((s, c) => s + parseFloat(c.outstanding || 0), 0);

  return (
    <div>
      <div style={{ ...card, padding: '12px 18px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 15, ...pg }}>{data.length} customers with outstanding balance</div>
        <div style={{ fontWeight: 800, fontSize: 17, color: '#dc2626' }}>
          Total Outstanding: ₹{(parseFloat(total) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {data.map(c => (
          <div key={c.id} style={{ ...card, padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ fontWeight: 800, fontSize: 15, ...pg, marginBottom: 2 }}>{c.name}</div>
              {c.customer_type === 'business' && (
                <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, background: darkMode ? '#1e3a5f' : '#dbeafe', color: darkMode ? '#93c5fd' : '#1d4ed8', marginRight: 6 }}>🏢 Business</span>
              )}
              {c.mobile && <span style={{ fontSize: 12, ...muted }}>📞 {c.mobile}</span>}
            </div>
            <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#dc2626' }}>
                  ₹{(parseFloat(c.outstanding) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </div>
                <div style={{ fontSize: 11, ...muted }}>Outstanding</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 700, ...muted }}>
                  ₹{(parseFloat(c.total_billed) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </div>
                <div style={{ fontSize: 11, ...muted }}>Total Billed</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: acc }}>{c.invoice_count}</div>
                <div style={{ fontSize: 11, ...muted }}>Invoices</div>
              </div>
              {c.mobile && (
                <div style={{ display: 'flex', gap: 6 }}>
                  <a href={`tel:${c.mobile}`} style={{ padding: '6px 14px', background: '#16a34a', color: '#fff', borderRadius: 7, textDecoration: 'none', fontWeight: 700, fontSize: 12 }}>📞 Call</a>
                  <button onClick={async (e) => { const btn = e.currentTarget; btn.disabled = true; btn.textContent = '⏳...'; try { const fd = new FormData(); fd.append('phone', c.mobile); fd.append('message', `Hello ${c.name || ''}`); await whatsappAPI.send(fd); btn.textContent = '✅ Sent'; } catch { btn.textContent = '❌ Failed'; } setTimeout(() => { btn.disabled = false; btn.textContent = '💬 WhatsApp'; }, 2000); }} style={{ padding: '6px 14px', background: '#25d366', color: '#fff', borderRadius: 7, border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>💬 WhatsApp</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const fmtDate = d => formatDateDMY(d, '');
const fmtNum = n => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });
const BILL_LABELS = { tax_invoice: 'TAX INVOICE', non_gst: 'BILL / RECEIPT', quotation: 'QUOTATION', bill_of_supply: 'BILL OF SUPPLY', proforma: 'PROFORMA INVOICE' };

const INDIA_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman & Diu',
  'Delhi', 'Jammu & Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry',
];

/* ──────────────────────────────────────────────
   CUSTOMER LEDGER MODAL
────────────────────────────────────────────── */
const CustomerLedgerModal = ({ customer, onClose, onPrint }) => {
  const { darkMode, color } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailTab, setDetailTab] = useState('invoices');
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerError, setLedgerError] = useState(null);
  const [ledgerFetched, setLedgerFetched] = useState(false);
  const [ledgerSending, setLedgerSending] = useState('');
  const acc = color.swatch;
  const cardBg = darkMode ? '#1E293B' : '#fff';
  const border = darkMode ? '#334155' : '#E2E8F0';
  const pg = { color: darkMode ? '#F1F5F9' : '#0F172A' };
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };

  useEffect(() => {
    customerAPI.invoices(customer.id).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [customer.id]);

  useEffect(() => {
    setLedgerFetched(false);
    setLedgerEntries([]);
    setLedgerError(null);
    setLedgerLoading(false);
  }, [customer.id]);

  useEffect(() => {
    if (detailTab === 'ledger' && !ledgerFetched && !ledgerLoading) {
      setLedgerLoading(true);
      setLedgerError(null);
      paymentAPI.getLedger('customer', customer.id)
        .then(r => { setLedgerEntries(Array.isArray(r.data?.entries) ? r.data.entries : Array.isArray(r.data) ? r.data : []); setLedgerFetched(true); })
        .catch((err) => { setLedgerEntries([]); setLedgerError(err.response?.data?.detail || 'Failed to load ledger'); setLedgerFetched(true); })
        .finally(() => setLedgerLoading(false));
    }
  }, [detailTab, customer.id, ledgerFetched, ledgerLoading]);

  const statusBadge = s => {
    const map = { paid: ['#dcfce7','#166534'], unpaid: ['#fee2e2','#991b1b'], partial: ['#fef9c3','#854d0e'] };
    const [bg, tx] = map[s] || ['#f1f5f9','#374151'];
    return <span style={{ padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: bg, color: tx }}>{s}</span>;
  };

  const sendLedgerVia = async (channel) => {
    setLedgerSending(channel);
    try {
      const phone = customer.mobile || customer.mobile2 || customer.mobile3 || '';
      if (channel === 'whatsapp' && !phone) {
        throw new Error('Customer phone number is required to send ledger via WhatsApp');
      }
      if (channel === 'email' && !customer.email) {
        throw new Error('Customer email is required to send ledger via Email');
      }

      await settingsAPI.sendLedger({
        party_type: 'customer',
        party_name: customer.name || 'Customer',
        channel,
        entries: ledgerEntries,
        recipient: customer.email || undefined,
        phone: phone || undefined,
      });
      alert(`✅ Ledger sent via ${channel}!`);
    } catch (e) {
      alert(`❌ ${e.response?.data?.detail || e.message || `Failed to send ledger via ${channel}`}`);
    }
    setLedgerSending('');
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <div className="animate-scaleIn" style={{ background: cardBg, borderRadius: 16, width: '100%', maxWidth: 'min(1120px, calc(100vw - 12px))', maxHeight: '98dvh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '18px 24px', borderBottom: `1px solid ${border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h3 style={{ ...pg, fontSize: 19, fontWeight: 800, margin: 0 }}>
              📊 {customer.name}
              {customer.customer_type === 'business' && <span style={{ marginLeft: 8, padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: '#dbeafe', color: '#1d4ed8' }}>🏢 Business</span>}
            </h3>
            <div style={{ display: 'flex', gap: 16, marginTop: 6, flexWrap: 'wrap' }}>
              {customer.mobile && <span style={{ ...muted, fontSize: 12 }}>📞 {customer.mobile}</span>}
              {customer.mobile2 && <span style={{ ...muted, fontSize: 12 }}>📞 {customer.mobile2}</span>}
              {customer.mobile3 && <span style={{ ...muted, fontSize: 12 }}>📞 {customer.mobile3}</span>}
              {customer.gstin && <span style={{ ...muted, fontSize: 11 }}>GSTIN: {customer.gstin}</span>}
              {customer.state && <span style={{ ...muted, fontSize: 11 }}>📍 {customer.state}</span>}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', ...muted, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 60, ...muted }}>Loading history…</div>
          ) : !data ? (
            <div style={{ textAlign: 'center', padding: 60, ...muted }}>Failed to load</div>
          ) : (
            <>
              <div style={{ padding: '14px 24px', background: darkMode ? '#0F172A' : '#F8FAFC', borderBottom: `1px solid ${border}` }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
                  {[['Total Spent', `₹${fmtNum(data.total_spent)}`, '#16a34a'], ['Pending', `₹${fmtNum(data.total_pending)}`, '#dc2626'], ['Invoices', data.invoice_count, acc]].map(([label, val, clr]) => (
                    <div key={label} style={{ textAlign: 'center', padding: '12px 8px', background: cardBg, borderRadius: 10, border: `1px solid ${border}` }}>
                      <div style={{ fontSize: 20, fontWeight: 800, color: clr }}>{val}</div>
                      <div style={{ ...muted, fontSize: 12, marginTop: 2 }}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Tab bar: Invoices | Ledger */}
              <div style={{ display: 'flex', gap: 4, padding: '10px 24px', borderBottom: `1px solid ${border}` }}>
                {[['invoices', '🧾 Invoices'], ['ledger', '📒 Ledger']].map(([key, label]) => (
                  <button key={key} onClick={() => setDetailTab(key)}
                    style={{ padding: '6px 16px', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer',
                      background: detailTab === key ? acc : 'transparent',
                      color: detailTab === key ? '#fff' : (darkMode ? '#94A3B8' : '#64748B') }}>
                    {label}
                  </button>
                ))}
              </div>
              {detailTab === 'invoices' && (
                <>
              {data.invoices.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, ...muted }}>No invoices found</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 680 }}>
                  <thead>
                    <tr style={{ background: darkMode ? '#0F172A' : '#F8FAFC', borderBottom: `1px solid ${border}` }}>
                      {['Invoice#', 'Type', 'Date', 'Amount', 'Mode', 'Status', ''].map(h => (
                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, ...muted, textTransform: 'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.invoices.map(inv => (
                      <tr key={inv.id} style={{ borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}` }}>
                        <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontWeight: 700, color: acc, fontSize: 12 }}>{inv.invoice_number}</td>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{ padding: '2px 7px', borderRadius: 5, fontSize: 10, fontWeight: 700, background: darkMode ? '#1e3a5f' : '#dbeafe', color: darkMode ? '#93c5fd' : '#1d4ed8' }}>
                            {BILL_LABELS[inv.bill_type]?.split(' ')[0] || 'GST'}
                          </span>
                        </td>
                        <td style={{ padding: '10px 14px', ...muted, fontSize: 12 }}>{fmtDate(inv.created_at)}</td>
                        <td style={{ padding: '10px 14px', fontWeight: 700, ...pg }}>₹{fmtNum(inv.grand_total)}</td>
                        <td style={{ padding: '10px 14px', ...muted, fontSize: 12 }}>{inv.payment_mode}</td>
                        <td style={{ padding: '10px 14px' }}>{statusBadge(inv.payment_status)}</td>
                        <td style={{ padding: '10px 14px' }}>
                          <button onClick={() => onPrint(inv.id)} style={{ background: 'none', border: 'none', color: '#16a34a', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>🖨️ Print</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
                </>
              )}
              {detailTab === 'ledger' && (
                ledgerLoading ? (
                  <div style={{ textAlign: 'center', padding: 40, ...muted }}>Loading ledger…</div>
                ) : ledgerError ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#DC2626' }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>⚠️</div>
                    {ledgerError}
                  </div>
                ) : ledgerEntries.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, ...muted }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>📒</div>
                    No ledger entries found
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 760 }}>
                    <thead>
                      <tr style={{ background: darkMode ? '#0F172A' : '#F8FAFC', borderBottom: `1px solid ${border}` }}>
                        {['Date', 'Type', 'Description', 'Mode', 'Debit', 'Credit', 'Balance'].map(h => (
                          <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, ...muted, textTransform: 'uppercase' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerEntries.map((entry, idx) => (
                        <tr key={entry.id || idx} style={{ borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}` }}>
                          <td style={{ ...pg, padding: '8px 12px', whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDate(entry.entry_date || entry.created_at)}</td>
                          <td style={{ padding: '8px 12px' }}>
                            <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 700,
                              background: entry.entry_type?.includes('in') ? '#DCFCE7' : '#FEE2E2',
                              color: entry.entry_type?.includes('in') ? '#16A34A' : '#DC2626' }}>
                              {entry.entry_type?.includes('in') ? '↓ In' : '↑ Out'}
                            </span>
                          </td>
                          <td style={{ ...pg, padding: '8px 12px', fontSize: 12 }}>{entry.description || '—'}</td>
                          <td style={{ ...muted, padding: '8px 12px', fontSize: 12 }}>{entry.payment_mode || '—'}</td>
                          <td style={{ padding: '8px 12px', color: '#DC2626', fontWeight: 600, fontSize: 12 }}>{entry.entry_type?.includes('out') ? `₹${fmtNum(entry.amount)}` : ''}</td>
                          <td style={{ padding: '8px 12px', color: '#16A34A', fontWeight: 600, fontSize: 12 }}>{entry.entry_type?.includes('in') ? `₹${fmtNum(entry.amount)}` : ''}</td>
                          <td style={{ ...pg, padding: '8px 12px', fontWeight: 700, fontSize: 12 }}>₹{fmtNum(Math.abs(entry.running_balance || 0))} {(entry.running_balance || 0) >= 0 ? 'Dr' : 'Cr'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )
              )}
            </>
          )}
        </div>
        <div style={{ padding: '12px 24px', borderTop: `1px solid ${border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {detailTab === 'ledger' ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                onClick={() => sendLedgerVia('whatsapp')}
                disabled={ledgerLoading || !!ledgerError || ledgerEntries.length === 0 || ledgerSending === 'whatsapp'}
                style={{ padding: '8px 14px', background: '#25D366', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', opacity: (ledgerLoading || !!ledgerError || ledgerEntries.length === 0 || ledgerSending === 'whatsapp') ? 0.6 : 1 }}>
                {ledgerSending === 'whatsapp' ? '⏳' : '💬'} WhatsApp
              </button>
              <button
                onClick={() => sendLedgerVia('telegram')}
                disabled={ledgerLoading || !!ledgerError || ledgerEntries.length === 0 || ledgerSending === 'telegram'}
                style={{ padding: '8px 14px', background: '#0088cc', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', opacity: (ledgerLoading || !!ledgerError || ledgerEntries.length === 0 || ledgerSending === 'telegram') ? 0.6 : 1 }}>
                {ledgerSending === 'telegram' ? '⏳' : '✈️'} Telegram
              </button>
              <button
                onClick={() => sendLedgerVia('email')}
                disabled={ledgerLoading || !!ledgerError || ledgerEntries.length === 0 || ledgerSending === 'email'}
                style={{ padding: '8px 14px', background: '#EA4335', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', opacity: (ledgerLoading || !!ledgerError || ledgerEntries.length === 0 || ledgerSending === 'email') ? 0.6 : 1 }}>
                {ledgerSending === 'email' ? '⏳' : '📧'} Email
              </button>
            </div>
          ) : <span />}
          <button onClick={onClose} style={{ padding: '9px 24px', background: darkMode ? '#334155' : '#F1F5F9', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', ...pg }}>Close</button>
        </div>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────
   ADD / EDIT CUSTOMER MODAL
────────────────────────────────────────────── */
const CustomerModal = ({ customer, onClose, onSaved }) => {
  const { darkMode, color } = useTheme();
  const acc = color.swatch;
  const cardBg = darkMode ? '#1E293B' : '#fff';
  const border = darkMode ? '#334155' : '#E2E8F0';
  const inp = { background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, width: '100%', outline: 'none', boxSizing: 'border-box' };
  const lbl = { color: darkMode ? '#94A3B8' : '#64748B', fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' };
  const req = { color: '#ef4444', marginLeft: 2 };
  const pg = { color: darkMode ? '#F1F5F9' : '#0F172A' };
  const muted = { color: darkMode ? '#94A3B8' : '#64748B' };
  const secTitle = { ...pg, fontSize: 13, fontWeight: 700, margin: '18px 0 10px', paddingBottom: 6, borderBottom: `1px solid ${border}` };

  const [form, setForm] = useState({
    name: customer?.name || '',
    customer_type: customer?.customer_type || 'normal',
    mobile: customer?.mobile || '',
    mobile2: customer?.mobile2 || '',
    mobile3: customer?.mobile3 || '',
    email: customer?.email || '',
    phone: customer?.phone || '',
    birthday: customer?.birthday ? customer.birthday.split('T')[0] : '',
    anniversary: customer?.anniversary ? customer.anniversary.split('T')[0] : '',
    address: customer?.address || '',
    state: customer?.state || 'Maharashtra',
    country: customer?.country || 'India',
    gstin: customer?.gstin || '',
    pan: customer?.pan || '',
    tags: (customer?.tags || ''),
    notes: customer?.notes || '',
    opening_balance: customer?.opening_balance ?? '',
    op_bal_type: customer?.op_bal_type || 'debit',
    contact_person: customer?.contact_person || '',
    contact_no: customer?.contact_no || '',
    document_type: customer?.document_type || '',
    document_no: customer?.document_no || '',
    doc_issue_date: customer?.doc_issue_date ? customer.doc_issue_date.split('T')[0] : '',
    credit_allowed: customer?.credit_allowed ?? true,
    credit_limit: customer?.credit_limit ?? '',
    price_category: customer?.price_category || 'retail',
    gst_type: customer?.gst_type || '',
    gst_verified: customer?.gst_verified ?? false,
    legal_name: customer?.legal_name || '',
  });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [autoSaved, setAutoSaved] = useState('');
  const [dupWarning, setDupWarning] = useState(null);  // { duplicates: [...] } when duplicate detected
  const autoSaveTimer = useRef(null);

  // Auto-save when editing existing customer
  useEffect(() => {
    if (!customer) return; // only in edit mode
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(async () => {
      try {
        const payload = { ...form, tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean).join(',') : '' };
        const res = await customerAPI.update(customer.id, payload, true);  // force=true: skip dup check for auto-save
        if (!res.data?.duplicate_warning) {
          setAutoSaved('✓ Auto-saved');
          setTimeout(() => setAutoSaved(''), 3000);
        }
      } catch (_) {}
    }, 2500);
    return () => clearTimeout(autoSaveTimer.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.name, form.customer_type, form.mobile, form.mobile2, form.mobile3, form.email,
      form.birthday, form.anniversary, form.address, form.state, form.gstin, form.pan, form.tags, form.notes, form.opening_balance,
      form.phone, form.country, form.op_bal_type, form.contact_person, form.contact_no,
      form.document_type, form.document_no, form.doc_issue_date, form.credit_allowed, form.credit_limit,
      form.price_category, form.gst_type, form.gst_verified, form.legal_name]);

  const set = k => e => { setDirty(true); setForm(f => ({ ...f, [k]: e.target.value })); };
  const isBusiness = form.customer_type === 'business';

  const handleClose = useCallback(() => {
    if (dirty && !window.confirm('Discard this customer? All unsaved changes will be lost.')) return;
    onClose();
  }, [dirty, onClose]);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [handleClose]);

  const handleSubmit = async (e, force = false) => {
    if (e) e.preventDefault();
    if (!form.name.trim()) { alert('Name is required'); return; }
    if (!form.mobile.trim()) { alert('Primary Mobile number is required'); return; }
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = null;
    setSaving(true);
    setDupWarning(null);
    const payload = {
      ...form,
      tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean).join(',') : '',
      opening_balance: form.opening_balance !== '' ? parseFloat(form.opening_balance) || 0 : 0,
      credit_limit: form.credit_limit !== '' ? parseFloat(form.credit_limit) || 0 : null,
    };
    try {
      let res;
      if (customer) res = await customerAPI.update(customer.id, payload, force);
      else res = await customerAPI.create(payload, force);
      setSaving(false);
      onSaved();
      return;
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.duplicate_warning) {
        setDupWarning(detail);
        setSaving(false);
        return;
      }
      const msg = (typeof detail === 'string' && detail) ? detail
        : (detail && typeof detail === 'object' && (Array.isArray(detail) ? detail.length > 0 : Object.keys(detail).length > 0)) ? JSON.stringify(detail)
        : 'Save failed';
      alert(msg);
    }
    setSaving(false);
  };

  const Section = ({ title }) => <div style={secTitle}>{title}</div>;
  const TypeBtn = ({ val, label, icon }) => (
    <button type="button" onClick={() => { setDirty(true); setForm(f => ({ ...f, customer_type: val })); }}
      style={{ flex: 1, padding: '10px 14px', border: `2px solid ${form.customer_type === val ? acc : border}`, borderRadius: 10, background: form.customer_type === val ? (darkMode ? '#1e3a5f' : '#eff6ff') : 'transparent', cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s' }}>
      <div style={{ fontSize: 20 }}>{icon}</div>
      <div style={{ fontSize: 12, fontWeight: 700, color: form.customer_type === val ? acc : (darkMode ? '#64748B' : '#94A3B8'), marginTop: 4 }}>{label}</div>
    </button>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 400, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 10, overflowY: 'auto' }}>
      <div className="animate-scaleIn" style={{ background: darkMode ? '#0F172A' : '#E2E8F0', borderRadius: 18, width: '100%', maxWidth: 'min(1120px, calc(100vw - 12px))', maxHeight: '98dvh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.3)', padding: 12, boxSizing: 'border-box', marginTop: 4 }}>
      <div style={{ background: cardBg, borderRadius: 14, border: `2px solid ${border}` }}>
        <div style={{ height: 4, background: `linear-gradient(90deg, ${acc}, ${acc}88)`, borderRadius: '14px 14px 0 0' }} />
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: cardBg, zIndex: 10 }}>
            <h3 style={{ ...pg, fontSize: 17, fontWeight: 800, margin: 0 }}>{customer ? '✏️ Edit Customer' : '➕ Add Customer'}
              {autoSaved && <span style={{ marginLeft: 12, fontSize: 12, fontWeight: 600, color: '#10B981' }}>{autoSaved}</span>}
            </h3>
            <button type="button" onClick={handleClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', ...muted }}>×</button>
          </div>

          <div style={{ padding: '16px' }}>
            {/* Customer Type */}
            <Section title="Customer Type" />
            <div style={{ display: 'flex', gap: 12, marginBottom: 4 }}>
              <TypeBtn val="normal" label="Normal Customer" icon="👤" />
              <TypeBtn val="business" label="Business Owner" icon="🏢" />
            </div>

            {/* Basic Info */}
            <Section title="Basic Information" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={lbl}>Full Name<span style={req}>*</span></label>
                <input style={{ ...inp, border: !form.name.trim() ? '1px solid #ef4444' : inp.border }} required value={form.name} onChange={set('name')} placeholder="Customer / business name" />
              </div>
              <div>
                <label style={lbl}>Email</label>
                <input style={inp} type="email" value={form.email} onChange={set('email')} placeholder="email@example.com" />
              </div>
              <div>
                <label style={lbl}>Tags (comma separated)</label>
                <input style={inp} value={form.tags} onChange={set('tags')} placeholder="vip, wholesale, retail" />
              </div>
            </div>

            {/* Contact Numbers */}
            <Section title="Contact Numbers (up to 3)" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <div>
                <label style={lbl}>Primary Mobile<span style={req}>*</span></label>
                <input style={{ ...inp, border: !form.mobile.trim() ? '1px solid #ef4444' : inp.border }} required value={form.mobile} onChange={set('mobile')} placeholder="+91 99999 99999" />
              </div>
              <div>
                <label style={lbl}>Mobile 2 (optional)</label>
                <input style={inp} value={form.mobile2} onChange={set('mobile2')} placeholder="Alternate number" />
              </div>
              <div>
                <label style={lbl}>Mobile 3 (optional)</label>
                <input style={inp} value={form.mobile3} onChange={set('mobile3')} placeholder="Third contact number" />
              </div>
              <div>
                <label style={lbl}>Landline / Phone</label>
                <input style={inp} value={form.phone} onChange={set('phone')} placeholder="Landline number" />
              </div>
            </div>

            {/* Address */}
            <Section title="Address" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={lbl}>Street / Area</label>
                <textarea style={{ ...inp, minHeight: 60, resize: 'vertical' }} value={form.address} onChange={set('address')} placeholder="Shop/House no., Street, Area, City, Pincode" />
              </div>
              <div>
                <label style={lbl}>State</label>
                <select style={inp} value={form.state} onChange={set('state')}>
                  {INDIA_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Country</label>
                <input style={inp} value={form.country} onChange={set('country')} placeholder="India" />
              </div>
            </div>

            {/* Business Details — only for business type */}
            {isBusiness && (
              <>
                <Section title="Business / GST Details" />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                  <div>
                    <label style={lbl}>GSTIN</label>
                    <input style={inp} value={form.gstin} onChange={set('gstin')} placeholder="27AABCU9603R1ZX" maxLength={15} />
                  </div>
                  <div>
                    <label style={lbl}>PAN Number</label>
                    <input style={inp} value={form.pan} onChange={set('pan')} placeholder="AABCU9603R" maxLength={10}
                      onInput={e => e.target.value = e.target.value.toUpperCase()} />
                  </div>
                  <div>
                    <label style={lbl}>GST Type</label>
                    <select style={inp} value={form.gst_type} onChange={set('gst_type')}>
                      <option value="">-- Select --</option>
                      <option value="Regular">Regular</option>
                      <option value="Composition">Composition</option>
                      <option value="Unregistered">Unregistered</option>
                      <option value="Consumer">Consumer</option>
                      <option value="SEZ">SEZ</option>
                    </select>
                  </div>
                  <div>
                    <label style={lbl}>Legal Name (as per GST)</label>
                    <input style={inp} value={form.legal_name} onChange={set('legal_name')} placeholder="Registered legal name" />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 18 }}>
                    <input type="checkbox" checked={form.gst_verified} onChange={e => { setDirty(true); setForm(f => ({ ...f, gst_verified: e.target.checked })); }} />
                    <label style={{ ...lbl, margin: 0 }}>GST Verified</label>
                  </div>
                  <div>
                    <label style={lbl}>Contact Person</label>
                    <input style={inp} value={form.contact_person} onChange={set('contact_person')} placeholder="Contact person name" />
                  </div>
                  <div>
                    <label style={lbl}>Contact Number</label>
                    <input style={inp} value={form.contact_no} onChange={set('contact_no')} placeholder="Contact person phone" />
                  </div>
                </div>

                <Section title="ID Document" />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                  <div>
                    <label style={lbl}>Document Type</label>
                    <select style={inp} value={form.document_type} onChange={set('document_type')}>
                      <option value="">-- None --</option>
                      <option value="Aadhaar">Aadhaar Card</option>
                      <option value="PAN">PAN Card</option>
                      <option value="Passport">Passport</option>
                      <option value="DrivingLicense">Driving License</option>
                      <option value="VoterID">Voter ID</option>
                    </select>
                  </div>
                  <div>
                    <label style={lbl}>Document Number</label>
                    <input style={inp} value={form.document_no} onChange={set('document_no')} placeholder="Document number" />
                  </div>
                  <div>
                    <label style={lbl}>Document Issue Date</label>
                    <StrictDateInput style={inp}  value={form.doc_issue_date} onChange={set('doc_issue_date')} />
                  </div>
                </div>

                <Section title="Credit & Pricing" />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 8 }}>
                    <input type="checkbox" checked={form.credit_allowed} onChange={e => { setDirty(true); setForm(f => ({ ...f, credit_allowed: e.target.checked })); }} />
                    <label style={{ ...lbl, margin: 0 }}>Credit Allowed</label>
                  </div>
                  <div>
                    <label style={lbl}>Credit Limit (₹)</label>
                    <input style={inp} type="number" step="0.01" value={form.credit_limit} onChange={set('credit_limit')} placeholder="0.00" />
                  </div>
                  <div>
                    <label style={lbl}>Price Category</label>
                    <select style={inp} value={form.price_category} onChange={set('price_category')}>
                      <option value="retail">Retail</option>
                      <option value="wholesale">Wholesale</option>
                      <option value="dealer">Dealer</option>
                      <option value="distributor">Distributor</option>
                    </select>
                  </div>
                </div>
              </>
            )}

            {/* Personal Dates — only for normal */}
            {!isBusiness && (
              <>
                <Section title="Personal Dates" />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                  <div>
                    <label style={lbl}>Birthday</label>
                    <StrictDateInput style={inp}  value={form.birthday} onChange={set('birthday')} />
                  </div>
                  <div>
                    <label style={lbl}>Anniversary</label>
                    <StrictDateInput style={inp}  value={form.anniversary} onChange={set('anniversary')} />
                  </div>
                </div>
              </>
            )}

            {/* Account Balance */}
            <Section title="Account Balance" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <div>
                <label style={lbl}>Opening Balance (₹)</label>
                <input style={inp} type="number" step="0.01" value={form.opening_balance} onChange={set('opening_balance')} placeholder="0.00" />
              </div>
              <div>
                <label style={lbl}>Balance Type</label>
                <select style={inp} value={form.op_bal_type} onChange={set('op_bal_type')}>
                  <option value="debit">Debit (Customer Owes)</option>
                  <option value="credit">Credit (You Owe)</option>
                </select>
              </div>
            </div>

            {/* Notes */}
            <Section title="Notes" />
            <textarea style={{ ...inp, minHeight: 54, resize: 'vertical' }} value={form.notes} onChange={set('notes')} placeholder="Any notes about this customer…" />

            {/* Duplicate Warning Banner */}
            {dupWarning && (
              <div style={{ marginTop: 16, padding: 14, background: '#fef3c7', border: '2px solid #f59e0b', borderRadius: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#92400e', marginBottom: 8 }}>⚠️ Duplicate Phone / Email Found</div>
                {(dupWarning.duplicates || []).map((d, i) => (
                  <div key={i} style={{ padding: '6px 0', borderTop: i > 0 ? '1px solid #fde68a' : 'none', fontSize: 13, color: '#78350f' }}>
                    <strong>{d.name}</strong> — {d.matched.join(', ')}
                    {d.mobile && <span style={{ marginLeft: 8, fontSize: 11, color: '#92400e' }}>📞 {d.mobile}</span>}
                  </div>
                ))}
                <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                  <button type="button" onClick={() => handleSubmit(null, true)}
                    style={{ padding: '7px 16px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                    Save Anyway
                  </button>
                  <button type="button" onClick={() => setDupWarning(null)}
                    style={{ padding: '7px 16px', background: 'transparent', border: '1px solid #f59e0b', borderRadius: 7, fontWeight: 600, fontSize: 12, cursor: 'pointer', color: '#92400e' }}>
                    Cancel & Fix
                  </button>
                </div>
              </div>
            )}
          </div>

          <div style={{ padding: '12px 16px', borderTop: `1px solid ${border}`, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" onClick={handleClose} style={{ padding: '9px 20px', background: 'transparent', border: `1px solid ${border}`, borderRadius: 8, fontWeight: 600, cursor: 'pointer', ...muted }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ padding: '9px 28px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
              {saving ? 'Saving…' : customer ? 'Save Changes' : 'Add Customer'}
            </button>
          </div>
        </form>
      </div>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────
   MAIN CUSTOMERS PAGE
────────────────────────────────────────────── */
const Customers = () => {
  const { darkMode, color } = useTheme();
  const { refreshCustomers } = useData();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const openedFromUrl = useRef(searchParams.get('action') === 'create');
  const sourceRoute = useRef(searchParams.get('source') || null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [showAddModal, setShowAddModal] = useState(searchParams.get('action') === 'create');
  const [editCustomer, setEditCustomer] = useState(null);
  const [ledgerCustomer, setLedgerCustomer] = useState(null);
  const [printInvoiceId, setPrintInvoiceId] = useState(null);
  const [printState, setPrintState] = useState(null);
  const [activeTab, setActiveTab] = useState('list');
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const searchRef = useRef(null);

  const acc = color.swatch;

  // Clear action/source params after reading
  useEffect(() => {
    if (searchParams.get('action') || searchParams.get('source')) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete('action');
        next.delete('source');
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const card = { background: darkMode ? '#1E293B' : '#fff', border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, borderRadius: 12 };
  const inp = { background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none' };
  const pg = { color: darkMode ? '#F1F5F9' : '#0F172A' };
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await customerAPI.list({ page: currentPage, limit: 15, search: searchTerm || undefined });
      const all = res.data.customers || [];
      const filtered = typeFilter ? all.filter(c => c.customer_type === typeFilter) : all;
      setCustomers(filtered);
      setTotalPages(res.data.pages || 1);
      setTotal(res.data.total || 0);
    } catch {}
    setLoading(false);
  }, [currentPage, searchTerm, typeFilter]);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);
  useEffect(() => { customerAPI.stats().then(r => setStats(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!printInvoiceId) return;
    Promise.all([invoiceAPI.get(printInvoiceId), settingsAPI.get()]).then(([invRes, shopRes]) => {
      setPrintState({ invoice: invRes.data, shop: shopRes.data });
      setPrintInvoiceId(null);
    }).catch(() => { alert('Failed to load invoice'); setPrintInvoiceId(null); });
  }, [printInvoiceId]);

  // Reset selection when customer list changes
  useEffect(() => { setSelectedIdx(-1); }, [customers]);

  // Listen for sv-open-modal event
  useEffect(() => {
    const handler = (e) => { if (e.detail === 'add-customer') { setEditCustomer(null); setShowAddModal(true); } };
    window.addEventListener('sv-open-modal', handler);
    return () => window.removeEventListener('sv-open-modal', handler);
  }, []);

  const handleDelete = async id => {
    if (!window.confirm('Delete this customer? Their invoices will NOT be deleted.')) return;
    try { await customerAPI.delete(id); loadCustomers(); refreshCustomers(true); } catch { alert('Delete failed'); }
  };

  // Keyboard navigation for customer list
  useEffect(() => {
    const onKey = (e) => {
      if (showAddModal || ledgerCustomer || printState) return;
      if (activeTab !== 'list') return;
      const tag = e.target.tagName;
      const inInput = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target.isContentEditable;
      if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, customers.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)); }
      else if (e.key === 'Escape') {
        if (selectedIdx >= 0) { setSelectedIdx(-1); }
        else if (sourceRoute.current) { const dest = sourceRoute.current; sourceRoute.current = null; navigate(dest, { replace: true }); }
      }
      else if (e.key === 'Enter' && selectedIdx >= 0 && !inInput) { const c = customers[selectedIdx]; if (c) { setEditCustomer(c); setShowAddModal(true); } }
      else if (e.key === 'Delete' && selectedIdx >= 0 && !inInput) { const c = customers[selectedIdx]; if (c) handleDelete(c.id); }
      else if ((e.key === 'n' || e.key === 'N') && !inInput) { e.preventDefault(); setEditCustomer(null); setShowAddModal(true); }
      else if ((e.key === '/' || e.key === 'f' || e.key === 'F') && !inInput) { e.preventDefault(); searchRef.current?.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, selectedIdx, showAddModal, ledgerCustomer, printState, activeTab]);

  const typeBadge = t => t === 'business'
    ? <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, background: darkMode ? '#1e3a5f' : '#dbeafe', color: darkMode ? '#93c5fd' : '#1d4ed8' }}>🏢 Biz</span>
    : <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, background: darkMode ? '#1a2e1a' : '#dcfce7', color: darkMode ? '#86efac' : '#15803d' }}>👤 Normal</span>;

  const tagBadge = tag => (
    <span key={tag} style={{ padding: '2px 7px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: darkMode ? '#1e3a5f' : '#dbeafe', color: darkMode ? '#93c5fd' : '#1e40af', marginRight: 3 }}>{tag}</span>
  );

  return (
    <Layout>
      <div className="page-enter" style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ ...pg, fontSize: 28, fontWeight: 800, margin: 0 }}>Customers</h1>
            <p style={{ ...muted, margin: '4px 0 0', fontSize: 14 }}>Manage your customers and view purchase history</p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button onClick={() => { setEditCustomer(null); setShowAddModal(true); }}
              style={{ padding: '10px 22px', background: acc, color: '#fff', border: 'none', borderRadius: 9, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
              ➕ Add Customer
            </button>
            <button onClick={async () => { try { const r = await bulkAPI.exportCustomers(); const url = URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = url; a.download = 'customers_export.xlsx'; a.click(); URL.revokeObjectURL(url); } catch { alert('Export failed'); } }}
              style={{ padding: '10px 16px', background: darkMode ? '#1E293B' : '#F1F5F9', color: darkMode ? '#94A3B8' : '#64748B', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 9, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              📤 Export
            </button>
            <label style={{ padding: '10px 16px', background: darkMode ? '#1E293B' : '#F1F5F9', color: darkMode ? '#94A3B8' : '#64748B', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 9, fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'inline-block' }}>
              📥 Import
              <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; try { const r = await bulkAPI.importCustomers(f); alert(`Import done! ${r.data.imported} imported, ${r.data.skipped} skipped.${r.data.errors?.length ? '\nErrors: ' + r.data.errors.join('\n') : ''}`); window.location.reload(); } catch (err) { alert('Import failed: ' + (err.response?.data?.detail || err.message)); } e.target.value = ''; }} />
            </label>
            <button onClick={async () => { try { const r = await bulkAPI.customerTemplate(); const url = URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = url; a.download = 'customers_template.xlsx'; a.click(); URL.revokeObjectURL(url); } catch { alert('Download failed'); } }}
              style={{ padding: '10px 12px', background: 'transparent', color: acc, border: 'none', borderRadius: 9, fontWeight: 600, fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
              📋 Template
            </button>
          </div>
        </div>

        {/* Tab bar */}
        <div style={{ borderBottom: `2px solid ${darkMode ? '#334155' : '#E2E8F0'}`, marginBottom: 20, display: 'flex', gap: 4 }}>
          {[{ id: 'list', label: '👥 All Customers' }, { id: 'outstanding', label: '💰 Outstanding' }].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
              padding: '9px 22px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13, background: 'transparent',
              borderBottom: activeTab === t.id ? `3px solid ${acc}` : '3px solid transparent',
              color: activeTab === t.id ? acc : (darkMode ? '#64748B' : '#94A3B8'), marginBottom: -2, transition: 'all 0.2s',
            }}>{t.label}</button>
          ))}
        </div>

        {activeTab === 'outstanding' && <CustomerOutstanding />}

        {activeTab === 'list' && (<>
        {/* Stats */}
        {stats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14, marginBottom: 20 }}>
            {[
              ['Total Customers', stats.total_customers ?? stats.total, darkMode ? '#F1F5F9' : '#0F172A'],
              ['New This Month', stats.new_this_month, '#3b82f6'],
              ['🎂 Birthdays Set', stats.with_birthday || 0, '#ec4899'],
              ['💍 Anniv. Set', stats.with_anniversary || 0, '#f59e0b'],
            ].map(([label, val, clr]) => (
              <div key={label} style={{ ...card, padding: '14px 18px' }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: clr }}>{val ?? '—'}</div>
                <div style={{ ...muted, fontSize: 12, marginTop: 2 }}>{label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div style={{ ...card, padding: '14px 16px', marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <input ref={searchRef} style={{ ...inp, flex: 1, minWidth: 200 }} placeholder="Search by name, phone, email, GSTIN…"
            value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
          <select style={inp} value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setCurrentPage(1); }}>
            <option value="">All Types</option>
            <option value="normal">👤 Normal Customers</option>
            <option value="business">🏢 Business Owners</option>
          </select>
        </div>

        {/* Table */}
        <div className="stagger-list" style={{ ...card, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 60, ...muted }}>Loading customers…</div>
          ) : customers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60 }}>
              <div style={{ fontSize: 56, marginBottom: 12 }}>👤</div>
              <p style={{ ...muted, marginBottom: 16 }}>No customers found</p>
              <button onClick={() => setShowAddModal(true)} style={{ padding: '10px 24px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Add First Customer</button>
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                <thead>
                  <tr style={{ background: darkMode ? '#0F172A' : '#F8FAFC', borderBottom: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                    {['Customer', 'Type', 'Contact', 'State', 'GSTIN / PAN', 'Tags', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, ...muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {customers.map((c, idx) => (
                    <tr key={c.id} style={{ borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}`, ...(selectedIdx === idx ? { background: darkMode ? '#334155' : '#EFF6FF', outline: `2px solid ${acc}`, outlineOffset: -2 } : {}), cursor: 'pointer' }} onClick={() => setSelectedIdx(idx)}>
                      <td style={{ padding: '11px 14px' }}>
                        <div style={{ fontWeight: 700, ...pg }}>{c.name}</div>
                        {c.email && <div style={{ fontSize: 11, ...muted, marginTop: 2 }}>✉ {c.email}</div>}
                        {parseFloat(c.outstanding) > 0 && (
                          <div style={{ marginTop: 3, display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, background: '#fee2e2', color: '#991b1b' }}>
                            ₹{(parseFloat(c.outstanding)||0).toLocaleString('en-IN',{minimumFractionDigits:2})} due
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '11px 14px' }}>{typeBadge(c.customer_type)}</td>
                      <td style={{ padding: '11px 14px', fontSize: 12 }}>
                        {c.mobile && <div style={pg}>{c.mobile}</div>}
                        {c.mobile2 && <div style={{ ...muted, fontSize: 11 }}>{c.mobile2}</div>}
                        {c.mobile3 && <div style={{ ...muted, fontSize: 11 }}>{c.mobile3}</div>}
                        {!c.mobile && <span style={muted}>—</span>}
                      </td>
                      <td style={{ padding: '11px 14px', fontSize: 12, ...muted }}>{c.state || '—'}</td>
                      <td style={{ padding: '11px 14px', fontSize: 11 }}>
                        {c.gstin && <div style={{ fontFamily: 'monospace', color: darkMode ? '#93c5fd' : '#1d4ed8' }}>G: {c.gstin}</div>}
                        {c.pan && <div style={{ fontFamily: 'monospace', ...muted }}>P: {c.pan}</div>}
                        {!c.gstin && !c.pan && <span style={muted}>—</span>}
                      </td>
                      <td style={{ padding: '11px 14px' }}>
                        {(Array.isArray(c.tags) ? c.tags : (c.tags || '').split(',').filter(Boolean)).slice(0, 3).map(t => tagBadge(t.trim()))}
                      </td>
                      <td style={{ padding: '11px 14px', whiteSpace: 'nowrap' }}>
                        <button onClick={() => setLedgerCustomer(c)} style={{ marginRight: 6, fontSize: 12, color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>📊 History</button>
                        <button onClick={() => { setEditCustomer(c); setShowAddModal(true); }} style={{ marginRight: 6, fontSize: 12, color: acc, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>✏️ Edit</button>
                        <button onClick={() => handleDelete(c.id)} style={{ fontSize: 12, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>🗑</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              {totalPages > 1 && (
                <div style={{ padding: '12px 16px', borderTop: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}
                    style={{ padding: '6px 16px', border: `1px solid ${darkMode ? '#334155' : '#CBD5E1'}`, borderRadius: 6, background: 'transparent', ...pg, cursor: 'pointer' }}>◀ Prev</button>
                  <span style={{ fontSize: 13, ...muted }}>Page {currentPage} of {totalPages} — {total} customers</span>
                  <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}
                    style={{ padding: '6px 16px', border: `1px solid ${darkMode ? '#334155' : '#CBD5E1'}`, borderRadius: 6, background: 'transparent', ...pg, cursor: 'pointer' }}>Next ▶</button>
                </div>
              )}
            </>
          )}
        </div>
        </>)}
      </div>

      {showAddModal && (
        <CustomerModal
          customer={editCustomer}
          onClose={() => {
            setShowAddModal(false); setEditCustomer(null);
            if (openedFromUrl.current) { openedFromUrl.current = false; const dest = sourceRoute.current || '/dashboard'; sourceRoute.current = null; navigate(dest, { replace: true }); }
          }}
          onSaved={() => { const fromUrl = openedFromUrl.current; const dest = sourceRoute.current; openedFromUrl.current = false; sourceRoute.current = null; setShowAddModal(false); setEditCustomer(null); loadCustomers(); refreshCustomers(true); if (fromUrl) navigate(dest || '/dashboard', { replace: true }); }}
        />
      )}

      {ledgerCustomer && (
        <CustomerLedgerModal
          customer={ledgerCustomer}
          onClose={() => setLedgerCustomer(null)}
          onPrint={id => { setLedgerCustomer(null); setPrintInvoiceId(id); }}
        />
      )}

      {/* Quick print from ledger */}
      {printState && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div className="animate-scaleIn" style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 'min(1120px, calc(100vw - 12px))', maxHeight: '98dvh', overflowY: 'auto' }}>
            <div style={{ padding: '14px 20px', background: '#1e293b', borderRadius: '12px 12px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#fff', fontWeight: 700 }}>🖨️ Invoice — {printState.invoice.invoice_number}</span>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => {
                  const node = document.getElementById('sv-cust-print-root');
                  if (!node) return;
                  const w = window.open('', '_blank', 'width=800,height=600');
                  if (!w) { alert('Pop-up blocked! Please allow pop-ups for this site.'); return; }
                  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Invoice</title><style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:Arial,sans-serif;background:#fff;color:#000}@page{margin:8mm;size:A4}img{max-width:100%}</style></head><body>${node.innerHTML}</body></html>`);
                  w.document.close(); w.focus(); setTimeout(() => { try { w.print(); } catch(e) {} }, 600);
                }} style={{ padding: '7px 18px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 700, cursor: 'pointer' }}>🖨️ Print</button>
                <button onClick={() => setPrintState(null)} style={{ padding: '7px 18px', background: '#475569', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 700, cursor: 'pointer' }}>Close</button>
              </div>
            </div>
            <div id="sv-cust-print-root">
              <QuickInvoicePrint inv={printState.invoice} shop={printState.shop} />
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

const QuickInvoicePrint = ({ inv, shop }) => {
  const fmtN = n => (parseFloat(n)||0).toLocaleString('en-IN', { minimumFractionDigits:2 });
  const billLabel = BILL_LABELS[inv.bill_type] || 'INVOICE';
  const isGST = inv.bill_type === 'tax_invoice' || inv.bill_type === 'bill_of_supply';
  const logoSrc = shop.logo_url ? resolveMediaUrl(shop.logo_url) : logo;
  return (
    <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, padding: '20px 28px' }}>
      <div style={{ background: '#1e3a5f', color: '#fff', padding: '16px 20px', margin: '-20px -28px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img
            src={logoSrc}
            alt=""
            onError={(e) => { e.currentTarget.src = logo; }}
            style={{ width: 42, height: 42, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
          />
          <div style={{ fontSize: 18, fontWeight: 800 }}>{shop.shop_name || 'Shop'} <span style={{ fontSize: 12, opacity: 0.7 }}>| {shop.shop_address}</span></div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: 700 }}>{billLabel}</div>
          <div style={{ fontSize: 12 }}>#{inv.invoice_number} · {fmtDate(inv.created_at)}</div>
        </div>
      </div>
      <div style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: 10, marginBottom: 14 }}>
        <strong>Customer:</strong> {inv.customer_name} {inv.customer_phone && `| ${inv.customer_phone}`}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 14 }}>
        <thead><tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
          {['Item', 'Qty', 'Rate', isGST && 'GST%', 'Total'].filter(Boolean).map(h => (
            <th key={h} style={{ padding: '7px 10px', textAlign: h === 'Item' ? 'left' : 'right', fontWeight: 700 }}>{h}</th>
          ))}
        </tr></thead>
        <tbody>{(inv.items || []).map((item, i) => (
          <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
            <td style={{ padding: '7px 10px' }}>{item.product_name}</td>
            <td style={{ padding: '7px 10px', textAlign: 'right' }}>{item.quantity}</td>
            <td style={{ padding: '7px 10px', textAlign: 'right' }}>₹{fmtN(item.price)}</td>
            {isGST && <td style={{ padding: '7px 10px', textAlign: 'right' }}>{item.gst_percent}%</td>}
            <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 700 }}>₹{fmtN(item.total)}</td>
          </tr>
        ))}</tbody>
      </table>
      <div style={{ textAlign: 'right', borderTop: '2px solid #e5e7eb', paddingTop: 10 }}>
        {isGST && <div style={{ fontSize: 12 }}>GST: ₹{fmtN(inv.gst_total)}</div>}
        <div style={{ fontSize: 17, fontWeight: 800, color: '#16a34a' }}>Total: ₹{fmtN(inv.grand_total)}</div>
      </div>
      {(shop.bank_account || shop.upi_id) && (
        <div style={{ marginTop: 12, padding: '10px 14px', background: '#eff6ff', borderRadius: 6, fontSize: 11 }}>
          {shop.bank_name && <span>Bank: {shop.bank_name} | </span>}
          {shop.bank_account && <span>A/c: {shop.bank_account} | </span>}
          {shop.bank_ifsc && <span>IFSC: {shop.bank_ifsc} | </span>}
          {shop.upi_id && <span>UPI: {shop.upi_id}</span>}
        </div>
      )}
    </div>
  );
};

export default Customers;
